import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

/** CSS px per mm (1in = 96px = 25.4mm). */
function mmToPx(mm: number): number {
  return (mm / 25.4) * 96;
}

export function PaperFit({
  widthMm,
  children,
  className,
  preserveLayout = false,
  userZoom = 1,
}: {
  widthMm: number;
  children: ReactNode;
  className?: string;
  /** Scale with transform so layout boxes stay 1:1 (needed for pagedjs). */
  preserveLayout?: boolean;
  /** Extra zoom on top of fit-to-width (may exceed 1). */
  userZoom?: number;
}) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [fitScale, setFitScale] = useState(1);
  const [innerHeight, setInnerHeight] = useState(0);
  const [useZoom] = useState(
    () =>
      !preserveLayout &&
      typeof CSS !== "undefined" &&
      CSS.supports("zoom", "0.5"),
  );

  const zoomFactor =
    Number.isFinite(userZoom) && userZoom > 0 ? userZoom : 1;
  const scale = fitScale * zoomFactor;

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const update = () => {
      const avail = outer.clientWidth;
      const natural = mmToPx(widthMm);
      if (natural <= 1 || avail <= 1) return;
      const next = Math.min(1, avail / natural);
      setFitScale((prev) => (Math.abs(prev - next) < 0.0015 ? prev : next));
      const nextHeight = inner.offsetHeight;
      setInnerHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    };

    const ro = new ResizeObserver(update);
    ro.observe(outer);
    ro.observe(inner);
    update();
    return () => ro.disconnect();
  }, [widthMm, zoomFactor]);

  const innerStyle: CSSProperties = useZoom
    ? { width: `${widthMm}mm`, zoom: scale }
    : {
        width: `${widthMm}mm`,
        transform: `scale(${scale})`,
        transformOrigin: "top center",
      };

  const outerStyle: CSSProperties | undefined = useZoom
    ? undefined
    : { height: innerHeight * scale || undefined };

  return (
    <div
      ref={outerRef}
      className={`sm-paper-fit${className ? ` ${className}` : ""}`}
      style={outerStyle}
    >
      <div ref={innerRef} className="sm-paper-fit-inner" style={innerStyle}>
        {children}
      </div>
    </div>
  );
}
